import { create } from 'zustand';
import { queryRecords, fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import type { LaborEmployee, LaborRecord } from '../types/timelabor';
import type { QueryOptions } from '../api/types';

const FE = FIELDS.laborEmployees;
const FL = FIELDS.forceAccountLabor;

function mapEmployee(r: Record<string, unknown>): LaborEmployee {
  return {
    id: r.record_id as number, employeeName: (r[FE.employeeName] as string) || '',
    employeeId: (r[FE.employeeId] as string) || '', jobTitle: (r[FE.jobTitle] as string) || '',
    department: (r[FE.department] as string) || '', employmentType: (r[FE.employmentType] as string) || '',
    flsaStatus: (r[FE.flsaStatus] as string) || '', budgetStatus: (r[FE.budgetStatus] as string) || '',
    regularRate: Number(r[FE.regularRate]) || 0, overtimeRate: Number(r[FE.overtimeRate]) || 0,
    totalFringePct: Number(r[FE.totalFringePct]) || 0, totalHourlyRate: Number(r[FE.totalHourlyRate]) || 0,
    rosterEmail: (r[FE.rosterEmail] as string) || '', organizationId: (r[FE.organizationId] as number) || null,
    normalWeeklyHours: Number(r[FE.normalWeeklyHours]) || 40, hireDate: (r[FE.hireDate] as string) || '',
    defaultAccountCode: (r[FE.defaultAccountCode] as string) || '', defaultProgramCode: (r[FE.defaultProgramCode] as string) || '',
    status: (r[FE.status] as string) || 'active', notes: (r[FE.notes] as string) || '',
  };
}

function mapRecord(r: Record<string, unknown>): LaborRecord {
  return {
    id: r.record_id as number, incidentId: (r[FL.incidentId] as number) || null,
    projectId: (r[FL.projectId] as number) || null, organizationId: (r[FL.organizationId] as number) || null,
    employeeName: (r[FL.employeeName] as string) || '', employeeId: (r[FL.employeeId] as string) || '',
    employeeTitle: (r[FL.employeeTitle] as string) || '', workDate: (r[FL.workDate] as string) || '',
    hoursRegular: Number(r[FL.hoursRegular]) || 0, hoursOvertime: Number(r[FL.hoursOvertime]) || 0,
    rateRegular: Number(r[FL.rateRegular]) || 0, rateOvertime: Number(r[FL.rateOvertime]) || 0,
    laborCost: Number(r[FL.laborCost]) || 0, fringeCost: Number(r[FL.fringeCost]) || 0,
    totalCost: Number(r[FL.totalCost]) || 0, fringeBenefitsPct: Number(r[FL.fringeBenefitsPct]) || 0,
    activityDescription: (r[FL.activityDescription] as string) || '', category: (r[FL.category] as string) || '',
    status: (r[FL.status] as string) || 'draft',
    approvedRegHours: Number(r[FL.approvedRegHours]) || 0, approvedOTHours: Number(r[FL.approvedOTHours]) || 0,
    approvedCost: Number(r[FL.approvedCost]) || 0, reviewerNotes: (r[FL.reviewerNotes] as string) || '',
    accountCode: (r[FL.accountCode] as string) || '', programCode: (r[FL.programCode] as string) || '',
    shiftPremium: (r[FL.shiftPremium] as string) || '', notes: (r[FL.notes] as string) || '',
  };
}

interface TimeLaborState {
  employees: LaborEmployee[]; employeesLoading: boolean;
  records: LaborRecord[]; recordsTotal: number; recordsLoading: boolean; recordsPage: number;
  fetchEmployees: () => Promise<void>; fetchRecords: (opts?: Partial<QueryOptions>) => Promise<void>;
  setRecordsPage: (p: number) => void;
  createEmployee: (data: Record<string, unknown>) => Promise<number | null>;
  updateEmployee: (id: number, data: Record<string, unknown>) => Promise<void>;
  createRecord: (data: Record<string, unknown>) => Promise<number | null>;
  updateRecord: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteRecord: (id: number) => Promise<void>;
  approveRecord: (id: number, approved: Record<string, unknown>) => Promise<void>;

  // V1 compat aliases
  laborRecords: LaborRecord[];
  loadingRecords: boolean;
  loadingEmployees: boolean;
  fetchLaborRecords: (opts?: Partial<QueryOptions>) => Promise<void>;
  createLaborRecord: (data: Record<string, unknown>) => Promise<number | null>;
  updateLaborRecord: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteLaborRecord: (id: number) => Promise<void>;
  deleteEmployee: (id: number) => Promise<void>;
}

export const useTimeLaborStore = create<TimeLaborState>((set, get) => ({
  employees: [], employeesLoading: false,
  records: [], recordsTotal: 0, recordsLoading: false, recordsPage: 1,

  fetchEmployees: async () => {
    set({ employeesLoading: true });
    try { const res = await fetchRecords<Record<string, unknown>>(TABLES.laborEmployees, { limit: 500 }); set({ employees: res.records.map(mapEmployee) }); }
    catch (e) { console.error('[TimeLab] fetchEmployees', e); }
    set({ employeesLoading: false });
  },

  fetchRecords: async (opts) => {
    set({ recordsLoading: true });
    try {
      const { recordsPage } = get();
      const res = await queryRecords<Record<string, unknown>>(TABLES.forceAccountLabor, {
        limit: 50, offset: (recordsPage - 1) * 50, sort: { field: 'created_at', direction: 'desc' }, ...opts,
      });
      set({ records: res.records.map(mapRecord), recordsTotal: res.total });
    } catch (e) { console.error('[TimeLab] fetchRecords', e); }
    set({ recordsLoading: false });
  },

  setRecordsPage: (p) => { set({ recordsPage: p }); get().fetchRecords(); },
  createEmployee: async (data) => { try { const r = await createRecord<Record<string, unknown>>(TABLES.laborEmployees, data); await get().fetchEmployees(); return (r as any).record_id; } catch (e) { console.error('[TimeLab]', e); return null; } },
  updateEmployee: async (id, data) => { try { await updateRecord(TABLES.laborEmployees, id, data); await get().fetchEmployees(); } catch (e) { console.error('[TimeLab]', e); } },
  createRecord: async (data) => { try { const r = await createRecord<Record<string, unknown>>(TABLES.forceAccountLabor, data); await get().fetchRecords(); return (r as any).record_id; } catch (e) { console.error('[TimeLab]', e); return null; } },
  updateRecord: async (id, data) => { try { await updateRecord(TABLES.forceAccountLabor, id, data); await get().fetchRecords(); } catch (e) { console.error('[TimeLab]', e); } },
  deleteRecord: async (id) => { try { await deleteRecord(TABLES.forceAccountLabor, id); await get().fetchRecords(); } catch (e) { console.error('[TimeLab]', e); } },
  approveRecord: async (id, approved) => { try { await updateRecord(TABLES.forceAccountLabor, id, { ...approved, [FL.status]: 'approved' }); await get().fetchRecords(); } catch (e) { console.error('[TimeLab]', e); } },

  // V1 compat aliases — initial values; real data accessed via store.records etc.
  laborRecords: [] as LaborRecord[],
  loadingRecords: false,
  loadingEmployees: false,
  fetchLaborRecords: async (opts?: Partial<QueryOptions>) => { await get().fetchRecords(opts); },
  createLaborRecord: async (data: Record<string, unknown>) => { return get().createRecord(data); },
  updateLaborRecord: async (id: number, data: Record<string, unknown>) => { return get().updateRecord(id, data); },
  deleteLaborRecord: async (id: number) => { return get().deleteRecord(id); },
  deleteEmployee: async (id: number) => { try { await deleteRecord(TABLES.laborEmployees, id); await get().fetchEmployees(); } catch (e) { console.error('[TimeLab]', e); } },
}));
